



import { useEffect, useReducer, useCallback, useMemo } from 'react';
// utils

//
import useSWR from 'swr';
// import axios, { fetcher, endpoints } from 'src/utils/axios';
import axiosInstance, { endpoints, fetcher } from 'src/utils/axios';
import { AuthContext } from './auth-context';
import { isValidToken, setSession } from './utils';
import { ActionMapType, AuthStateType, AuthUserType } from '../../types';



enum Types {
  INITIAL = 'INITIAL',
  LOGIN = 'LOGIN',
  REGISTER = 'REGISTER',
  LOGOUT = 'LOGOUT',
}

type Payload = {
  [Types.INITIAL]: {
    user: AuthUserType;
  };
  [Types.LOGIN]: {
    user: AuthUserType;
  };
  [Types.REGISTER]: {
    user: AuthUserType;
  };
  [Types.LOGOUT]: undefined;
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

// ----------------------------------------------------------------------

const initialState: AuthStateType = {
  user: null,
  loading: true,
};
axiosInstance.defaults.withCredentials = true;

const reducer = (state: AuthStateType, action: ActionsType) => {
  if (action.type === Types.INITIAL) {
    return {
      loading: false,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGIN) {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === Types.REGISTER) {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGOUT) {
    return {
      ...state,
      user: null,
    };
  }
  return state;
};

// ----------------------------------------------------------------------



type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const initialize = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');

      if (accessToken) {
        setSession(accessToken);

        const id = localStorage.getItem('id')

        const res = await axiosInstance.get(endpoints.auth.me, { params: { id } });

        //  console.log("iiiiiiiiiiiiiiiii",res)
        const user = res?.data;

        dispatch({
          type: Types.INITIAL,
          payload: {
            user,
          },
        });
      } else {
        dispatch({
          type: Types.INITIAL,
          payload: {
            user: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: Types.INITIAL,
        payload: {
          user: null,
        },
      });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  // const login = useCallback(async (email: string, password: string) => {
  //   const data = {
  //     email,
  //     password,
  //   };

  

  //   const { accessToken, user } = res.data;

  //   setSession(accessToken);

  //   dispatch({
  //     type: Types.LOGIN,
  //     payload: {
  //       user,
  //     },
  //   });
  // }, []);

  const login = useCallback(async (email: string, password: string) => {
    // console.log('🔐 [AUTH PROVIDER] Login request initiated', { email, endpoint: endpoints.auth.login });

    const res = await axiosInstance.post(
      endpoints.auth.login,
      {
        email,
        password,
      },
      {
        withCredentials: true,
      }
    );
    // console.log('🔐 [AUTH PROVIDER] Login response received:', res);

    // console.log('📥 [AUTH PROVIDER] Login response received:', {
    //   status: res.status,
    //   hasData: !!res.data,
    //   data: res.data,
    //   requires2FA: res?.data?.requires2FA,
    //   twoFactorRequired: res?.data?.twoFactorRequired,
    // });

    
    // Check user object for 2FA settings
    const userData = res?.data?.user;
    
    // Check for various possible 2FA indicators in response root
    const possible2FAFields = [
      'requires2FA',
      'twoFactorRequired',
      'requires_2fa',
      'two_factor_required',
      '2fa_required',
      'needs2FA',
      'needs_2fa',
      'otp_required',
      'otpRequired',
      'otp_sent',
      'otpSent',
    ];
    
    // Check for 2FA indicators in user object
    const possibleUser2FAFields = [
      'two_factor_enabled',
      'twoFactorEnabled',
      '2fa_enabled',
      '2faEnabled',
      'otp_enabled',
      'otpEnabled',
      'has_2fa',
      'has2FA',
    ];
    
    const found2FAField = possible2FAFields.find(field => res?.data?.[field] === true);
    const foundUser2FAField = userData ? possibleUser2FAFields.find(field => userData[field] === true) : null;
    
    // console.log('🔍 [AUTH PROVIDER] Checking for 2FA indicators:', {
    //   responseRootFields: possible2FAFields,
    //   foundResponseField: found2FAField,
    //   foundResponseValue: found2FAField ? res?.data?.[found2FAField] : null,
    //   userFields: possibleUser2FAFields,
    //   foundUserField: foundUser2FAField,
    //   foundUserValue: foundUser2FAField ? userData[foundUser2FAField] : null,
    //   userObject: userData,
    // });

    // Check if 2FA is required - check multiple possible field names in response root
    
    const requires2FA = res?.data?.requires2FA || 
                       res?.data?.twoFactorRequired || 
                       res?.data?.requires_2fa ||
                       res?.data?.two_factor_required ||
                       res?.data?.['2fa_required'] ||
                       res?.data?.needs2FA ||
                       res?.data?.needs_2fa ||
                       res?.data?.otp_required ||
                       res?.data?.otpRequired ||
                       res?.data?.otp_sent ||
                       res?.data?.otpSent;
    
    // Also check if user has 2FA enabled (might need verification even if not explicitly required)
    const userHas2FA = userData && (
      userData.two_factor_enabled ||
      userData.twoFactorEnabled ||
      userData['2fa_enabled'] ||
      userData['2faEnabled'] ||
      userData.otp_enabled ||
      userData.otpEnabled ||
      userData.has_2fa ||
      userData.has2FA
    );
    
    // TEMPORARY: For testing 2FA - remove this after backend is configured
    // Uncomment the line below to force 2FA for all users, or modify the condition
    // const FORCE_2FA_FOR_TESTING = true;
    const FORCE_2FA_FOR_TESTING = false; // Set to true to test 2FA flow
    
    // You can also test with specific user IDs:
    // const FORCE_2FA_FOR_TESTING = userData?.id === 41; // Test with user ID 41
    
    // console.log('🔍 [AUTH PROVIDER] 2FA Check Results:', {
    //   requires2FA,
    //   userHas2FA,
    //   FORCE_2FA_FOR_TESTING,
    //   willTrigger2FA: requires2FA || userHas2FA || FORCE_2FA_FOR_TESTING,
    // });
    
    if (requires2FA || userHas2FA || FORCE_2FA_FOR_TESTING) {
      // console.log('🔐 [AUTH PROVIDER] 2FA is required for this user', {
      //   requires2FA: res?.data?.requires2FA,
      //   twoFactorRequired: res?.data?.twoFactorRequired,
      //   requires_2fa: res?.data?.requires_2fa,
      //   two_factor_required: res?.data?.two_factor_required,
      //   '2fa_required': res?.data?.['2fa_required'],
      //   needs2FA: res?.data?.needs2FA,
      //   needs_2fa: res?.data?.needs_2fa,
      //   otp_required: res?.data?.otp_required,
      //   otpRequired: res?.data?.otpRequired,
      //   userHas2FA,
      //   FORCE_2FA_FOR_TESTING,
      // });
      
      // Store temporary login data for 2FA verification
      const tempData = {
        email,
        password,
        tempToken: res?.data?.tempToken || res?.data?.token,
        userId: res?.data?.userId || res?.data?.user?.id,
      };
      
      // console.log('💾 [AUTH PROVIDER] Storing 2FA temp data:', { ...tempData, password: '***' });
      sessionStorage.setItem('2fa_temp_data', JSON.stringify(tempData));
      
      // Throw a special error that indicates 2FA is required
      const error: any = new Error('2FA_REQUIRED');
      error.requires2FA = true;
      error.email = email;
      throw error;
    }

    // console.log('✅ [AUTH PROVIDER] No 2FA required, proceeding with normal login');

    // console.log("resresres",res)
    const user = res?.data?.user

    const accessToken = res?.data?.token;
    const refresh_token = res?.data?.refresh_token;
    const id = user?.id
    const LoginEmail = user?.email

    // console.log('💾 [AUTH PROVIDER] Storing user data in localStorage', { id, email: LoginEmail });

    localStorage.setItem('id', id)
   localStorage.setItem('refresh_token', refresh_token)
    localStorage.setItem('LoginEmail', LoginEmail)
    localStorage.setItem('loginUserName', user?.name)
    localStorage.setItem('role', user?.role)
    localStorage.setItem('company_id', user?.company_id)
    localStorage.setItem('servicesEnabled', user?.services_enabled)
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('updatedDate', user?.created_at);
    localStorage.setItem('userCompanyId',  user?.companies[0].id);

    setSession(accessToken);

    // console.log('✅ [AUTH PROVIDER] Dispatching LOGIN action');

    dispatch({
      type: Types.LOGIN,
      payload: {
        user,
      },
    });
  }, []);

  // REGISTER
  const register = useCallback(
    async (email: string, password: string, firstName: string, lastName: string) => {
      const data = {
        email,
        password,
        firstName,
        lastName,
      };

      const res = await axiosInstance.post(endpoints.auth.register, data);

      const { accessToken, user } = res.data;
      sessionStorage.setItem('accessToken', accessToken);
      dispatch({
        type: Types.REGISTER,
        payload: {
          user,
        },
      });
    },
    []
  );

  // VERIFY 2FA
  const verify2FA = useCallback(async (otp: string) => {
    
    const tempDataStr = sessionStorage.getItem('2fa_temp_data');
    if (!tempDataStr) {
      throw new Error('No pending 2FA verification found');
    }

    const tempData = JSON.parse(tempDataStr);
   

    const res = await axiosInstance.post(
      endpoints.auth.verify2FA,
      {
        email: tempData.email,
        otp,
        tempToken: tempData.tempToken,
      },
      {
        withCredentials: true,
      }
    );

    // console.log("resresres",res)

    const user = res?.data?.user;
    const accessToken = res?.data?.token;
    const id = user.id;
    const LoginEmail = user.email;
    const refresh_token = res?.data?.refresh_token;

    // Clear temporary 2FA data
    sessionStorage.removeItem('2fa_temp_data');
   localStorage.setItem('refresh_token', refresh_token)
    localStorage.setItem('id', id);
    localStorage.setItem('LoginEmail', LoginEmail);
    localStorage.setItem('loginUserName', user.name);
    localStorage.setItem('role', user.role);
    localStorage.setItem('company_id', user?.company_id);
    localStorage.setItem('servicesEnabled', user.services_enabled);
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('updatedDate', user.created_at);
    localStorage.setItem('userCompanyId', user.companies[0].id);



    setSession(accessToken);

    dispatch({
      type: Types.LOGIN,
      payload: {
        user,
      },
    });
    
  }, []);

  // RESEND 2FA CODE
  const resend2FACode = useCallback(async (email?: string) => {
    const tempDataStr = sessionStorage.getItem('2fa_temp_data');
    if (!tempDataStr) {
      throw new Error('No pending 2FA verification found');
    }

    const tempData = JSON.parse(tempDataStr);
    const emailToUse = email || tempData.email;

    if (!emailToUse) {
      throw new Error('Email is required to resend 2FA code');
    }

    const res = await axiosInstance.post(
      endpoints.auth.resend2FA,
      {
        email: emailToUse,
      },
      {
        withCredentials: true,
      }
    );

    // Update temp token if provided
    if (res?.data?.tempToken) {
      tempData.tempToken = res.data.tempToken;
      sessionStorage.setItem('2fa_temp_data', JSON.stringify(tempData));
    }

    return res.data;
  }, []);

  // LOGOUT https://reactstaging.beyondcloud.org.in/api/v1/logout
  // const logout = useCallback(async () => {
  //   localStorage.removeItem('role')
  //   sessionStorage.removeItem('2fa_temp_data');
  //   setSession(null);
  //   localStorage.removeItem('accessToken');
  //   localStorage.removeItem('company_id');
  //   localStorage.removeItem('id');
  //   dispatch({
  //     type: Types.LOGOUT,
  //   });
  // }, []);

const logout = useCallback(async () => {
  try {
    await axiosInstance.get(endpoints.auth.logout);
  } catch (error) {
    console.error('Logout API failed:', error);
    // still continue logout locally
  } finally {
    // Always clear client state
    localStorage.removeItem('role');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('company_id');
    localStorage.removeItem('id');
    localStorage.removeItem('LoginEmail');
    localStorage.removeItem('loginUserName');

    sessionStorage.removeItem('2fa_temp_data');

    // setSession(null);
    dispatch({ type: Types.LOGOUT });
  }
}, [dispatch]);




  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: 'jwt',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      //
      login,
      register,
      logout,
      verify2FA,
      resend2FACode,
    }),
    [login, logout, register, verify2FA, resend2FACode, state.user, status]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}

// company dropdwon

interface CompanyChild {
  id: number;
  name: string;
  children?: CompanyChild[];
}

type CompanyHierarchyResponse = CompanyChild[];

export function useGetCompanyDropdownWithChildren() {
  const companyId = localStorage.getItem('company_id');
  const user_id = localStorage.getItem('id')
  // If no company ID, skip fetch
  const shouldFetch = !!companyId;
  const URL = shouldFetch
    // ? `${endpoints.comany.companyDropdown}?company_id=${companyId}`
    ? `${endpoints.comany.companyDropdown}?user_id=${user_id}`
    : null;
  const { data, isLoading, error, isValidating } = useSWR<CompanyHierarchyResponse>(
    URL,
    fetcher
  );


  const memoized = useMemo(
    () => ({
      companyOptions: data || [],
      loading: isLoading,
      error,
      validating: isValidating,
      empty: !isLoading && (!data || data.length === 0),
    }),
    [data, isLoading, error, isValidating]
  );
  return memoized;
}


